/**
 * ForwardExposureLine — the dossier's ONE forward number, on Ryan's ladder
 * formula (Lane 713, Ryan's D50.4 + his Tier-3 override of the consult).
 *
 * D50.4: "ONE forward number — Ryan's ladder formula everywhere" (D45.4, latest
 * price × trailing-12-month volume). The deck (page 7, slide 10) was always
 * there; Lane 708 put the chat there. This is the dossier's half.
 *
 * ⚠️ THIS COMPONENT FORMATS NOTHING. Every string it renders is a token the
 * backend built in `helpers/deck_forward._forward_exposure_facts` — the SAME
 * dict page 7 and slide 10 render and the chat quotes — so the dollar a buyer
 * reads here is byte-for-byte the dollar on the slide. That is the whole reason
 * `forward_exposure_facts` was extracted as a public function (Lane 708): a
 * second formatter is how two surfaces come to differ by a dollar on one
 * number. There is deliberately no `fmtSignedDollars` import here.
 *
 * ⚠️ WHY THIS IS A SEPARATE COMPONENT FROM `ForwardRunRateLine`, AND WHY THAT
 * IS NOT THE DUPLICATION LANE 382 CLOSED. Lane 382 merged two hand-rolled
 * copies of ONE sentence that differed only in colour and formatter. These two
 * are different sentences about different quantities on different bases, and
 * **they are mutually exclusive by construction**: when
 * `DOSSIER_FORWARD_LADDER_ENABLED` is on the server pops
 * `forward_run_rate_surface_usd` at every window, so `ForwardRunRateLine`
 * renders nowhere on the page; when it is off, this component receives no facts
 * and renders nothing. One forward line or none — never both.
 *
 * ⚠️ IT IS A PANEL-LEVEL LINE, NOT A TILE LINE, AND THAT PLACEMENT IS THE
 * DECISION — not a layout preference. The 6/12/24 period tiles are PAIRING
 * tiles: `analytics.py` emits the PPI forward key only when
 * `(raw_your > 0) == (f1 > 0)` and `f1 > 0`, i.e. presence implies the tile's
 * own "Your Price Change" is POSITIVE, because pairing rules P3/P6 hold that
 * the % and the dollar are the same statement. This ladder figure is SIGNED and
 * derived independently, so dropping it into that tile would put a percentage
 * saying prices ROSE directly above a dollar saying we SAVE money. Measured on
 * real data, 5 of the 17 affected suppliers would have shown exactly that —
 * Amorim Cork Composites at "Your Price Change +1.02%" over "−$793", and Eriks
 * BV at "−3.11%" over "+$24,855". consult-architect caught it, the check it
 * named CONFIRMED it, and Ryan chose this placement with those numbers in hand.
 * Do not move this line back inside a period tile.
 *
 * The tone is taken from the SIGN CHARACTER of the server's own signed string
 * (`_format_signed_dollars` always leads with '+' or '-'), deliberately NOT
 * from the `fexp_cost_rows` token in the same dict: that token is the deck
 * `business_impact` slide's block-presence gate, and tinting off it would make
 * a future deck decision silently re-colour the dossier — the cross-surface
 * coupling this whole decision exists to end.
 */
import React from 'react';

/**
 * Props:
 *   facts — the `forward_exposure` object from
 *           /api/analytics/supplier-price-impact/{name}?months=12, or null.
 *           Absent/!facts renders nothing rather than an empty label, so a
 *           flag-off or fail-soft response can never leave an orphan eyebrow
 *           over blank space.
 */
export default function ForwardExposureLine({ facts }) {
  if (!facts || !facts.fexp_further || !facts.fexp_label) return null;

  const display = String(facts.fexp_further);
  // Three exact cases, no parsing: the server's formatter emits '+$…', '-$…'
  // or exactly '+$0'. A zero is neither a cost nor a saving and is tinted as
  // neither — the alternative (folding it into "cost") would print $0 in the
  // cost-increase red.
  let tone = 'cost-increase';
  let color = 'var(--up)';
  if (display.startsWith('-')) {
    tone = 'saving';
    color = 'var(--good)';
  } else if (display === '+$0') {
    tone = 'no-change';
    color = 'var(--text-2)';
  }

  return (
    <div className="sd-note" data-testid="dossier-forward-exposure">
      <span>{facts.fexp_label}: </span>
      {/* `data-tone` is the assertable twin of the colour: jsdom drops var()
          colours out of the style attribute, so a style assertion on the tint
          is vacuous (it passes whatever the colour is). The sign case is the
          one that can regress silently, so it is asserted on this attribute. */}
      <span
        data-tone={tone}
        data-testid="dossier-forward-exposure-value"
        style={{ color, fontWeight: 'var(--font-weight-semibold)' }}
      >
        {display}
      </span>
      {/* The BASKET AND THE WINDOW ARE RENDERED, NOT JUST COMPUTED — the same
          disclosure page 7 carries. `fexp_spent` is not what we paid this
          supplier: it sums only the parts the ladder could measure, so the
          basket is what makes the figure checkable rather than merely stated.
          D45.5: the window is the projection's VOLUME source, which is not the
          12 months the label projects forward, and a reader is owed both. */}
      <div style={{ marginTop: '2px' }} data-testid="dossier-forward-exposure-basis">
        {facts.fexp_projected} projected against {facts.fexp_spent} paid{' '}
        {facts.fexp_basket}, {facts.fexp_window} (projection). {facts.fexp_note}
      </div>
    </div>
  );
}
